import { useEffect, type MouseEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "@/lib/icons";
import { useApp } from "@/hooks/use-app-context";
import { LanguageSelector } from "@/components/ui/language-selector";
import { visitorHeaderText } from "./visitor-header-copy";
import type { VisitorNavItem } from "./visitor-nav";

interface VisitorMobileMenuProps {
  open: boolean;
  onClose: () => void;
  lang: string;
  items: VisitorNavItem[];
  onFollow: (item: VisitorNavItem) => (event: MouseEvent) => void;
}

/** Visitor menu below 1024 px: the same links as the header, then account, theme and language. */
export function VisitorMobileMenu({ open, onClose, lang, items, onFollow }: VisitorMobileMenuProps) {
  const { t, theme, toggleTheme, language } = useApp();
  const copy = visitorHeaderText(language);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  // A route change closes the menu.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => onClose(), [pathname]);

  const row = "flex h-11 items-center gap-3 rounded-lg px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted/60 focus-ring";

  return (
    <>
      <div
        aria-hidden="true"
        onClick={onClose}
        className={`fixed inset-0 z-[55] bg-black/50 transition-opacity duration-200 lg:hidden ${open ? "opacity-100" : "pointer-events-none opacity-0"}`}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={copy.menu}
        {...(!open && { inert: "" })}
        className={`fixed inset-y-0 right-0 z-[60] flex w-[86vw] max-w-sm flex-col overflow-y-auto border-l border-border/60 bg-background transition-transform duration-300 ease-premium lg:hidden ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-border/60 px-4">
          <span className="text-base font-semibold tracking-tight text-foreground">
            Yodi <span className="text-muted-foreground">Data Hub</span>
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label={copy.closeMenu}
            className="grid size-10 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-ring"
          >
            <Icon name="x" className="size-4" />
          </button>
        </div>

        <nav className="space-y-0.5 p-3" aria-label={copy.mobileNav}>
          {items.map((item) => (
            <Link key={item.href} to={item.href} onClick={onFollow(item)} className={row}>
              {item.label}
            </Link>
          ))}
          <Link to={`/${lang}/complaints`} onClick={onClose} className={`${row} text-muted-foreground`}>
            {copy.report}
          </Link>
        </nav>

        <div className="grid gap-2 border-t border-border/60 p-4">
          <Link
            to={`/${lang}/signup`}
            className="inline-flex h-11 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-ring"
          >
            {t.signup}
          </Link>
          <Link
            to={`/${lang}/login`}
            className="inline-flex h-11 items-center justify-center rounded-full border border-border text-sm font-medium text-foreground transition-colors hover:bg-muted/60 focus-ring"
          >
            {t.login}
          </Link>
        </div>

        <div className="mt-auto flex items-center justify-between gap-3 border-t border-border/60 px-4 py-3">
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={theme === "dark" ? copy.toLight : copy.toDark}
            className="inline-flex h-10 items-center gap-2 rounded-full px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted/60 focus-ring"
          >
            <Icon name={theme === "dark" ? "sun" : "moon"} className="size-4" />
            {theme === "dark" ? copy.light : copy.dark}
          </button>
          <LanguageSelector variant="minimal" />
        </div>
      </aside>
    </>
  );
}
